import { useCallback, useEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import arrowDown from '~/assets/icons/arrow_down.svg';
import { playSound, Sound } from '~/assets/sounds';
import { ImgIcon } from '../ImgIcon';
import { SelectDropdown } from '../SelectDropdown';
import { Text } from '../Text';
import {
	StyledArrowDownContainer,
	StyledContainer,
	StyledDropdownContainer,
	StyledTopContainer,
} from './Select.styled';

interface ISelectProps {
	items: string[];
	value: string;
	onChange: (value: string | null) => void;
	dropdownWidth?: string;
	dropdownHeight?: string;
}

const Select: React.FC<ISelectProps> = ({
	items,
	value,
	onChange,
	dropdownWidth,
	dropdownHeight,
}: ISelectProps) => {
	const [resultsVisible, setResultsVisible] = useState<boolean>(false);
	const resultsRef = useRef<HTMLDivElement>(null);

	const handleOpen = useCallback(() => {
		setResultsVisible((prevState) => !prevState);
	}, []);

	const handleSelect = useCallback(
		(item: string) => {
			if (!resultsVisible) {
				return;
			}

			onChange(item);
			setResultsVisible(false);
		},
		[onChange, resultsVisible],
	);

	useEffect(() => {
		function handleClickOutside(event: Event) {
			if (
				resultsRef.current &&
				!resultsRef.current.contains(event.target as Node)
			) {
				setResultsVisible(false);
			}
		}

		let timeoutId: NodeJS.Timeout;

		if (resultsVisible) {
			timeoutId = setTimeout(() => {
				document.addEventListener('mousedown', handleClickOutside);
			}, 300);
		}

		return () => {
			clearTimeout(timeoutId);
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [resultsRef, resultsVisible]);

	return (
		<StyledContainer ref={resultsRef}>
			<StyledTopContainer
				$focus={resultsVisible}
				onClick={() => {
					handleOpen();
					playSound(Sound.ClickButton);
				}}
				onMouseEnter={() => playSound(Sound.LinkCursor)}
			>
				<Text>{value}</Text>
				<StyledArrowDownContainer>
					<ImgIcon icon={arrowDown} width={12} height={12} />
				</StyledArrowDownContainer>
			</StyledTopContainer>
			<CSSTransition
				in={resultsVisible}
				classNames={'results'}
				timeout={500}
				unmountOnExit
			>
				<StyledDropdownContainer
					$width={dropdownWidth}
					$height={dropdownHeight}
				>
					<SelectDropdown
						value={value}
						items={items}
						handleSelect={handleSelect}
						visible={resultsVisible}
						dropdownHeight={dropdownHeight}
					/>
				</StyledDropdownContainer>
			</CSSTransition>
		</StyledContainer>
	);
};

export { Select };
